<template>
  <!-- Written text is left as placeholder lines, so nothing reads as a real post -->
  <div
    v-if="option === 'facebook' || option === 'linkedin'"
    class="absolute left-1/2 top-8 md:top-10 w-[26rem] max-w-[calc(100%-2rem)] -translate-x-1/2 rounded-lg bg-white shadow-xl ring-1 ring-black/5"
  >
    <div class="flex items-center gap-2.5 p-3">
      <img
        :src="avatarUrl"
        alt=""
        class="size-9 rounded-full"
      />
      <div class="grow leading-tight">
        <p class="text-sm font-semibold text-heading">
          {{ $t('your_name') }}
        </p>
        <div
          v-if="option === 'linkedin'"
          class="my-1 h-1.5 w-24 rounded-full bg-gray-200"
        />
        <p class="flex items-center gap-1 text-xs text-supplement">
          {{ $t('just_now', 'Just now') }} ·
          <Icon
            icon="globe"
            prefix="far"
            class="text-[10px]"
          />
        </p>
      </div>
      <Icon
        v-if="option === 'linkedin'"
        icon="linkedin"
        prefix="fab"
        class="self-start text-xl text-[#0a66c2]"
      />
    </div>
    <div class="space-y-1.5 px-3 pb-3">
      <div class="h-2 w-full rounded-full bg-gray-200" />
      <div class="h-2 w-4/5 rounded-full bg-gray-200" />
    </div>
    <div class="aspect-[1.91/1] overflow-hidden bg-gray-100">
      <img
        v-if="imageUrl"
        :src="imageUrl"
        alt=""
        class="size-full object-cover"
      />
    </div>
    <div class="bg-gray-50 px-3 py-2">
      <p class="text-[11px] uppercase text-supplement">
        {{ domain }}
      </p>
      <p class="text-sm font-semibold text-heading">
        {{ partnerName }}
      </p>
    </div>
  </div>

  <!-- A story with the link sticker that takes viewers to the advisor's link -->
  <div
    v-else-if="option === 'instagram'"
    class="absolute left-1/2 top-8 md:top-10 w-44 -translate-x-1/2 rounded-[1.75rem] bg-gray-900 p-1.5 shadow-xl"
  >
    <div class="relative aspect-[9/16] overflow-hidden rounded-[1.375rem] bg-gray-700">
      <img
        v-if="imageUrl"
        :src="imageUrl"
        alt=""
        class="size-full object-cover"
      />
      <div class="absolute inset-x-0 top-0 bg-gradient-to-b from-black/40 to-transparent p-2.5 pb-6">
        <div class="flex gap-1">
          <div class="h-0.5 grow rounded-full bg-white" />
          <div class="h-0.5 grow rounded-full bg-white/50" />
        </div>
        <div class="mt-2 flex items-center gap-1.5">
          <img
            :src="avatarUrl"
            alt=""
            class="size-6 rounded-full ring-1 ring-white"
          />
          <span class="text-[11px] font-semibold text-white">{{ $t('your_name') }}</span>
        </div>
      </div>
      <span class="absolute left-1/2 top-24 flex max-w-[90%] -translate-x-1/2 items-center gap-1 rounded-lg bg-white px-2 py-1.5 text-[10px] font-semibold text-[#0095f6] shadow">
        <Icon
          icon="link"
          prefix="far"
        />
        <span class="truncate">{{ domain }}</span>
      </span>
    </div>
  </div>

  <!-- A sent message with its link preview, as WhatsApp shows it, and the client's reply -->
  <div
    v-else-if="option === 'whatsapp'"
    class="absolute left-1/2 top-8 w-80 max-w-[calc(100%-2rem)] -translate-x-1/2 space-y-2 md:top-10"
  >
    <div class="ml-auto rounded-lg rounded-tr-none bg-[#d9fdd3] p-1 shadow-xl">
      <div class="flex overflow-hidden rounded-md bg-[#d1f4cc]">
        <div class="size-16 shrink-0 bg-gray-200">
          <img
            v-if="imageUrl"
            :src="imageUrl"
            alt=""
            class="size-full object-cover"
          />
        </div>
        <div class="min-w-0 px-2.5 py-2">
          <p class="text-xs font-semibold text-heading">
            {{ partnerName }}
          </p>
          <div class="mt-1.5 h-1.5 w-32 max-w-full rounded-full bg-black/10" />
          <p class="mt-1.5 text-[11px] text-supplement">
            {{ domain }}
          </p>
        </div>
      </div>
      <div class="px-1.5 pt-2">
        <div class="h-2 w-2/3 rounded-full bg-black/10" />
      </div>
      <div class="flex items-end justify-between gap-2 px-1.5 pb-0.5 pt-1.5">
        <span class="truncate text-xs text-[#027eb5]">{{ link }}</span>
        <span class="shrink-0 text-[10px] text-gray-500">{{ $t('just_now', 'Just now') }} <span class="text-[#53bdeb]">✓✓</span></span>
      </div>
    </div>
    <div class="mr-16 space-y-1.5 rounded-lg rounded-tl-none bg-white p-2.5 shadow-xl">
      <div class="h-2 w-full rounded-full bg-gray-200" />
      <div class="h-2 w-1/2 rounded-full bg-gray-200" />
    </div>
  </div>

  <!-- A drafted email with the link in its body -->
  <div
    v-else-if="option === 'email'"
    class="absolute left-1/2 top-8 md:top-10 w-[26rem] max-w-[calc(100%-2rem)] -translate-x-1/2 overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-black/5"
  >
    <div class="flex items-center justify-between bg-gray-800 px-3 py-2 text-xs font-semibold text-white">
      {{ $t('new_message', 'New message') }}
      <Icon
        icon="envelope"
        class="text-gray-400"
      />
    </div>
    <div class="divide-y px-3 text-xs text-supplement">
      <div class="flex items-center gap-2 py-2">
        {{ $t('to', 'To') }}
        <div class="h-2 w-28 rounded-full bg-gray-200" />
      </div>
      <div class="flex items-center gap-2 py-2">
        {{ $t('subject', 'Subject') }}
        <div class="h-2 w-40 rounded-full bg-gray-300" />
      </div>
    </div>
    <div class="space-y-1.5 px-3 pb-3 pt-2">
      <div class="h-2 w-full rounded-full bg-gray-200" />
      <div class="h-2 w-11/12 rounded-full bg-gray-200" />
      <p class="truncate pt-1 text-xs font-medium text-[#1a73e8] underline">
        {{ link }}
      </p>
      <div class="h-2 w-3/4 rounded-full bg-gray-200" />
    </div>
    <div class="mx-3 aspect-[1.91/1] overflow-hidden rounded bg-gray-100">
      <img
        v-if="imageUrl"
        :src="imageUrl"
        alt=""
        class="size-full object-cover"
      />
    </div>
  </div>

  <!-- The code on its own card, shown whole -->
  <div
    v-else-if="option === 'qrcode'"
    class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-4 shadow-xl ring-1 ring-black/5"
  >
    <!-- Looks like a code but isn't one, so it can't be scanned to the wrong place -->
    <svg
      viewBox="0 0 25 25"
      class="size-24 text-gray-900"
      shape-rendering="crispEdges"
    >
      <path
        :d="qrPath"
        fill="currentColor"
      />
    </svg>
  </div>
</template>

<script setup lang="ts">
import type { ShareOption } from '~/types/partner'

const avatarUrl = '/avatar-jennifer-raezer.png'

// A fixed pattern with the three corner squares every QR code has
const qrModules = (() => {
  const size = 25
  const modules: [number, number][] = []
  const isFinder = (x: number, y: number) => [[0, 0], [size - 7, 0], [0, size - 7]].some(([fx, fy]) => x >= fx! - 1 && x <= fx! + 7 && y >= fy! - 1 && y <= fy! + 7)

  for (const [fx, fy] of [[0, 0], [size - 7, 0], [0, size - 7]] as const) {
    for (let y = 0; y < 7; y++) {
      for (let x = 0; x < 7; x++) {
        const isRing = x === 0 || y === 0 || x === 6 || y === 6
        const isCentre = x >= 2 && x <= 4 && y >= 2 && y <= 4
        if (isRing || isCentre) modules.push([fx + x, fy + y])
      }
    }
  }

  let seed = 7
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      seed = (seed * 9301 + 49297) % 233280
      if (!isFinder(x, y) && seed / 233280 < 0.5) modules.push([x, y])
    }
  }

  return modules
})()

// One shape, so the rotated card doesn't show seams between squares
const qrPath = qrModules.map(([x, y]) => `M${x} ${y}h1v1h-1z`).join('')

defineProps<{
  option: ShareOption
  imageUrl: string | null
  domain: string
  // Where the link is written out in full, as in a message
  link: string
  partnerName: string
}>()
</script>
